import React, { useMemo } from 'react';

import { getBounds } from './geometry';

// Render a kraj outline preserving its true aspect ratio. Two distortions had to
// be undone vs. the previous projection-into-fixed-rect approach:
//   1. The fixed 520x300 viewport stretched shapes to fill it (Středočeský looked
//      squashed). Solved by computing the viewBox from the geometry itself and
//      letting SVG's preserveAspectRatio="xMidYMid meet" do the centered scale.
//   2. At Czech latitudes (~50°N) one degree of longitude is geographically much
//      shorter than one degree of latitude (cos(50°) ≈ 0.643). Without correcting
//      this, every shape stretched east-west. Solved by multiplying lon coords by
//      cos(midLat).
const ringToPath = (ring, lonScale) =>
    ring
        .map(([lon, lat], i) => {
            const x = (lon * lonScale).toFixed(5);
            const y = (-lat).toFixed(5);
            return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
        })
        .join(' ')
        .concat(' Z');

const geometryToAspectAwarePath = (geometry, lonScale) => {
    if (geometry.type === 'Polygon') {
        return geometry.coordinates.map(ring => ringToPath(ring, lonScale)).join(' ');
    }
    if (geometry.type === 'MultiPolygon') {
        return geometry.coordinates.flatMap(polygon => polygon.map(ring => ringToPath(ring, lonScale))).join(' ');
    }
    return '';
};

export const RegionShape = ({ feature }) => {
    const { viewBox, path } = useMemo(() => {
        const bounds = getBounds([feature]);
        const midLat = (bounds.minLat + bounds.maxLat) / 2;
        const lonScale = Math.cos((midLat * Math.PI) / 180);

        const minX = bounds.minLon * lonScale;
        const maxX = bounds.maxLon * lonScale;
        const minY = -bounds.maxLat; // flip: north up
        const maxY = -bounds.minLat;
        const dx = maxX - minX;
        const dy = maxY - minY;
        const padX = dx * 0.05;
        const padY = dy * 0.05;

        return {
            viewBox: `${(minX - padX).toFixed(5)} ${(minY - padY).toFixed(5)} ${(dx + padX * 2).toFixed(5)} ${(
                dy + padY * 2
            ).toFixed(5)}`,
            path: geometryToAspectAwarePath(feature.geometry, lonScale),
        };
    }, [feature]);

    return (
        <svg
            viewBox={viewBox}
            preserveAspectRatio="xMidYMid meet"
            style={{ width: '100%', maxWidth: 560, maxHeight: 320, height: 'auto' }}
        >
            <path
                d={path}
                fill="#f5f7fb"
                fillRule="evenodd"
                stroke="#c80707"
                strokeWidth="3"
                vectorEffect="non-scaling-stroke"
            />
        </svg>
    );
};
